import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext';
import { CartProvider } from './context/CartContext';
import SessionWarningDialog from './components/SessionWarningDialog';

// Lazy load pages
const VendorLogin = lazy(() => import('./pages/vendor/VendorLogin'));
const VendorDashboard = lazy(() => import('./pages/vendor/VendorDashboard'));
const VendorScan = lazy(() => import('./pages/vendor/VendorScan'));
const VendorOrders = lazy(() => import('./pages/vendor/VendorOrders'));
const VendorTransactions = lazy(() => import('./pages/vendor/VendorTransactions'));
const VendorCounters = lazy(() => import('./pages/vendor/VendorCounters'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminEmployees = lazy(() => import('./pages/admin/AdminEmployees'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminVendors = lazy(() => import('./pages/admin/AdminVendors'));
const AdminVendorDetails = lazy(() => import('./pages/admin/AdminVendorDetails'));
const AdminManagers = lazy(() => import('./pages/admin/AdminManagers'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const EmployeeLogin = lazy(() => import('./pages/employee/EmployeeLogin'));
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'));
const EmployeeOrder = lazy(() => import('./pages/employee/EmployeeOrder'));
const EmployeeOrders = lazy(() => import('./pages/employee/EmployeeOrders'));
const EmployeeTransactions = lazy(() => import('./pages/employee/EmployeeTransactions'));
const EmployeeScanPay = lazy(() => import('./pages/employee/EmployeeScanPay'));
const CounterLogin = lazy(() => import('./pages/counter/CounterLogin'));
const CounterDashboard = lazy(() => import('./pages/counter/CounterDashboard'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));


// Loading screen
const LoadingScreen = () => (
  <div style={{
    display: 'flex', justifyContent: 'center',
    alignItems: 'center', height: '100vh',
    flexDirection: 'column', gap: 16
  }}>
    <div style={{ fontSize: 48 }}>🍽️</div>
    <div style={{ fontSize: 18, color: '#666' }}>Loading...</div>
  </div>
);

// Protected Route for Vendor
const VendorRoute = ({ children }: { children: React.ReactNode }) => {
  const { role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (role !== 'vendor') return <Navigate to="/vendor/login" replace />;
  return <>{children}</>;
};

// Protected Route for Admin
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (role !== 'super_admin' && role !== 'manager') return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
};

// Protected Route for Employee
const EmployeeRoute = ({ children }: { children: React.ReactNode }) => {
  const { role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (role !== 'employee') return <Navigate to="/employee/login" replace />;
  return <>{children}</>;
};

// Gate for online food-ordering pages. When the feature is disabled (default),
// direct navigation redirects back to the role's dashboard. Scan & Pay is never
// gated by this.
const FoodOrderingRoute = ({ children, fallback }: { children: React.ReactNode; fallback: string }) => {
  const { foodOrderingEnabled, loading } = useFeatureFlags();
  if (loading) return <LoadingScreen />;
  if (!foodOrderingEnabled) return <Navigate to={fallback} replace />;
  return <>{children}</>;
};

// Root redirect based on role
const RootRedirect = () => {
  const { role } = useAuth();
  if (role === 'vendor') return <Navigate to="/vendor/dashboard" replace />;
  if (role === 'super_admin' || role === 'manager') return <Navigate to="/admin/dashboard" replace />;
  if (role === 'employee') return <Navigate to="/employee/dashboard" replace />;
  return <Navigate to="/vendor/login" replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Vendor Routes */}
        <Route path="/vendor/login" element={<VendorLogin />} />
        <Route path="/vendor/dashboard" element={
          <VendorRoute><VendorDashboard /></VendorRoute>
        } />
        <Route path="/vendor/scan" element={
          <VendorRoute><VendorScan /></VendorRoute>
        } />
        <Route path="/vendor/orders" element={
          <VendorRoute><FoodOrderingRoute fallback="/vendor/dashboard"><VendorOrders /></FoodOrderingRoute></VendorRoute>
        } />
        <Route path="/vendor/transactions" element={
          <VendorRoute><VendorTransactions /></VendorRoute>
        } />
        <Route path="/vendor/counters" element={
          <VendorRoute><VendorCounters /></VendorRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={
          <AdminRoute><AdminDashboard /></AdminRoute>
        } />
        <Route path="/admin/employees" element={
          <AdminRoute><AdminEmployees /></AdminRoute>
        } />
        <Route path="/admin/categories" element={
          <AdminRoute><AdminCategories /></AdminRoute>
        } />
        <Route path="/admin/vendors" element={
          <AdminRoute><AdminVendors /></AdminRoute>
        } />
        <Route path="/admin/vendors/:vendorId" element={
          <AdminRoute><AdminVendorDetails /></AdminRoute>
        } />
        <Route path="/admin/managers" element={
          <AdminRoute><AdminManagers /></AdminRoute>
        } />
        <Route path="/admin/settings" element={
          <AdminRoute><AdminSettings /></AdminRoute>
        } />
        <Route path="/admin/reports" element={
          <AdminRoute><AdminReports /></AdminRoute>
        } />

        {/* Employee Routes */}
        <Route path="/employee/login" element={<EmployeeLogin />} />
        <Route path="/employee/dashboard" element={
          <EmployeeRoute><EmployeeDashboard /></EmployeeRoute>
        } />
        <Route path="/employee/order" element={
          <EmployeeRoute><FoodOrderingRoute fallback="/employee/dashboard"><EmployeeOrder /></FoodOrderingRoute></EmployeeRoute>
        } />
        <Route path="/employee/orders" element={
          <EmployeeRoute><FoodOrderingRoute fallback="/employee/dashboard"><EmployeeOrders /></FoodOrderingRoute></EmployeeRoute>
        } />
        <Route path="/employee/transactions" element={
          <EmployeeRoute><EmployeeTransactions /></EmployeeRoute>
        } />
        <Route path="/employee/scan-pay" element={
          <EmployeeRoute><EmployeeScanPay /></EmployeeRoute>
        } />

        {/* Counter Routes (PIN-based login, no auth context) */}
        <Route path="/counter/login" element={<CounterLogin />} />
        <Route path="/counter/dashboard" element={<CounterDashboard />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <FeatureFlagProvider>
            <CartProvider>
              <AppRoutes />
              <SessionWarningDialog />
              <Toaster
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: { borderRadius: 8, fontFamily: 'Roboto, Arial, sans-serif' },
                  success: { iconTheme: { primary: '#4CAF50', secondary: '#fff' } },
                  error: { iconTheme: { primary: '#f44336', secondary: '#fff' } },
                }}
              />
            </CartProvider>
          </FeatureFlagProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
